// src/components/carrers/ApplyForm.tsx
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Upload, X, FileText } from "lucide-react";
import { useRef } from "react";

interface ApplyFormProps {
  jobTitle: string;
  formData: any;
  setFormData: (data: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  onFileDrop: (e: React.DragEvent) => void;
  onFileSelect: (file: File | null) => void;
  removeFile: () => void;
}

export function ApplyForm({
  jobTitle,
  formData,
  setFormData,
  onSubmit,
  onBack,
  onFileDrop,
  onFileSelect,
  removeFile,
}: ApplyFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <h3 className="text-lg font-semibold mb-4">
        {t('careers.applyForm.title', { jobTitle })}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t('careers.applyForm.firstName')} *</Label>
          <Input 
            name="firstName" 
            value={formData.firstName} 
            onChange={handleInputChange} 
            required 
            placeholder={t('careers.applyForm.firstName')}
          />
        </div>
        <div>
          <Label>{t('careers.applyForm.lastName')} *</Label>
          <Input 
            name="lastName" 
            value={formData.lastName} 
            onChange={handleInputChange} 
            required 
            placeholder={t('careers.applyForm.lastName')}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t('careers.applyForm.email')} *</Label>
          <Input 
            name="email" 
            type="email" 
            value={formData.email} 
            onChange={handleInputChange} 
            required 
            placeholder={t('careers.applyForm.email')}
          />
        </div>
        <div>
          <Label>{t('careers.applyForm.phone')} *</Label>
          <Input 
            name="phone" 
            type="tel" 
            value={formData.phone} 
            onChange={handleInputChange} 
            required 
            placeholder={t('careers.applyForm.phone')}
          />
        </div>
      </div>

      <div>
        <Label>{t('careers.applyForm.linkedin')}</Label>
        <Input
          name="linkedin"
          value={formData.linkedin}
          onChange={handleInputChange}
          placeholder={t('careers.applyForm.linkedinPlaceholder')}
        />
      </div>

      <div>
        <Label>{t('careers.applyForm.resume')} *</Label>
        <div
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-slate-400 transition-colors"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onFileDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mx-auto h-10 w-10 text-slate-400 mb-3" />
          <p 
            className="text-sm text-slate-600 dark:text-slate-400"
            dangerouslySetInnerHTML={{
              __html: t('careers.applyForm.resumeUploadText')
                .replace('<1>', '<span class="text-blue-600 underline">')
                .replace('</1>', '</span>')
            }}
          />
          <p className="text-xs text-slate-500 mt-1">
            {t('careers.applyForm.resumeMaxSize')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
          />
        </div>

        {formData.resumeFileName && (
          <div className="mt-3 flex items-center justify-between bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-blue-600" />
              <span className="truncate max-w-xs">{formData.resumeFileName}</span>
            </div>
            <button type="button" onClick={removeFile} className="text-red-600 hover:text-red-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div>
        <Label>{t('careers.applyForm.coverLetter')}</Label>
        <Textarea
          name="coverLetter"
          rows={4}
          value={formData.coverLetter}
          onChange={handleInputChange}
          placeholder={t('careers.applyForm.coverLetterPlaceholder')}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="agreeTerms"
          checked={formData.agreeTerms}
          onCheckedChange={(c) => setFormData((prev: any) => ({ ...prev, agreeTerms: c as boolean }))}
          required
        />
        <Label htmlFor="agreeTerms" className="text-sm">
          {t('careers.applyForm.agreeTerms')} *
        </Label>
      </div>

      <div className="flex gap-3 justify-end pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          {t('careers.applyForm.backToJobs')}
        </Button>
        <Button type="submit" disabled={!formData.resumeFile}>
          {t('careers.applyForm.submitApplication')}
        </Button>
      </div>
    </form>
  );
}